import './localized-string-input.scss';
import {useState} from 'react';
import {
  Box,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Badge,
  Tooltip,
  Typography,
} from '@mui/material';
import {LocalizedString} from '../../backend/interfaces/localized-string';
import {BackendLanguageTag} from '../../backend/enums/backend-language-tag';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import LanguageIcon from '@mui/icons-material/Language';

export const LocalizedStringInput = (props: {
  defaultValueLabel: string;
  localizedValuesLabel: string;
  defaultValue: string;
  handleChangeDefaultValue: (value: string) => void;
  handleChangeLocalizedValues: (values: LocalizedString) => void;
  localizedValues?: LocalizedString;
  defaultValueRequired?: boolean;
  localizedValueRequired?: boolean;
  defaultValueError?: string;
  localizedValuesError?: string;
  disabled?: boolean;
  multiline?: boolean;
  rows?: number;
  id?: string;
  className?: string;
  handleBlurDefaultValue?: () => void;
  handleBlurLocalizedValues?: () => void;
}) => {
  // Hold state
  const [localizedValuesModalOpened, setLocalizedValuesModalOpened] = useState<boolean>(false);
  const [localizedValues, setLocalizedValues] = useState<LocalizedString>({
    enUS: props.localizedValues?.enUS || '',
    esES: props.localizedValues?.esES || '',
    glES: props.localizedValues?.glES || '',
    ptPT: props.localizedValues?.ptPT || '',
    ptBR: props.localizedValues?.ptBR || '',
  });

  // Translations
  const {t} = useTranslation();

  // Handle the opened state of the localized string modal
  const handleLocalizedValuesModalOpened = (value: boolean) => {
    setLocalizedValuesModalOpened(value);
  };

  // Cancel the localized values
  const handleCancelLocalizedValues = () => {
    // Close the modal
    handleLocalizedValuesModalOpened(false);

    // Reset the values back
    setLocalizedValues({
      enUS: props.localizedValues?.enUS || '',
      esES: props.localizedValues?.esES || '',
      glES: props.localizedValues?.glES || '',
      ptPT: props.localizedValues?.ptPT || '',
      ptBR: props.localizedValues?.ptBR || '',
    });
  };

  // Confirm the localized values
  const handleConfirmLocalizedValues = () => {
    // Send the values back
    props.handleChangeLocalizedValues(localizedValues);

    // Close the modal
    handleLocalizedValuesModalOpened(false);
  };

  // Handle the change of a localized value
  const handleChangeLocalizedValue = (
    key: keyof LocalizedString,
    value: string | undefined | null
  ) => {
    // Process value
    let finalValue: string = '';
    if (value !== undefined && value !== null) {
      finalValue = value;
    }

    // Set it
    setLocalizedValues((prevState) => {
      return {
        ...prevState,
        [key]: finalValue,
      };
    });
  };

  // Handle click on the language button
  const handleClickLanguage = () => {
    // Open the modal
    handleLocalizedValuesModalOpened(true);

    // Blur
    if (props.handleBlurDefaultValue) {
      props.handleBlurDefaultValue();
    }
    if (props.handleBlurLocalizedValues) {
      props.handleBlurLocalizedValues();
    }
  };

  // Localized values count
  const localizedValuesCount: number = props.localizedValues
    ? Object.values(props.localizedValues).filter((value) => value.trim().length > 0).length
    : 0;

  return (
    <>
      <Box className={props.className} id={props.id}>
        <TextField
          label={props.defaultValueLabel}
          value={props.defaultValue}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            props.handleChangeDefaultValue(event.target.value)
          }
          onBlur={props.handleBlurDefaultValue}
          helperText={props.defaultValueError || props.localizedValuesError || undefined}
          error={props.defaultValueError || props.localizedValuesError ? true : false}
          disabled={props.disabled === true ? true : false}
          required={props.defaultValueRequired === true ? true : false}
          multiline={props.multiline === true ? true : false}
          rows={props.rows || 2}
          InputLabelProps={{
            shrink: true,
          }}
          InputProps={{
            endAdornment: (
              <Tooltip
                title={
                  localizedValuesCount > 1
                    ? t(translations.localized_string_input.has_many_translations_tooltip_message, {
                        count: localizedValuesCount,
                        total: Object.keys(BackendLanguageTag).length,
                      })
                    : localizedValuesCount === 1
                      ? t(translations.localized_string_input.has_one_translation_tooltip_message, {
                          total: Object.keys(BackendLanguageTag).length,
                        })
                      : t(
                          translations.localized_string_input.has_zero_translations_tooltip_message,
                          {
                            total: Object.keys(BackendLanguageTag).length,
                          }
                        )
                }
                placement="bottom"
              >
                <IconButton
                  disabled={props.disabled === true ? true : false}
                  onClick={handleClickLanguage}
                  color={props.localizedValuesError ? 'error' : 'default'}
                >
                  <Badge
                    badgeContent={localizedValuesCount}
                    color={props.localizedValuesError ? 'error' : 'primary'}
                  >
                    <LanguageIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            ),
          }}
          variant="outlined"
          color="neutral"
          type="text"
          fullWidth
        />
      </Box>
      <Dialog
        scroll="paper"
        onClose={handleCancelLocalizedValues}
        open={localizedValuesModalOpened}
      >
        <DialogTitle>{props.localizedValuesLabel}</DialogTitle>
        <DialogContent className="localized-string-input-dialog-content-container">
          {Object.entries(BackendLanguageTag).map((obj) => {
            return (
              <TextField
                key={`localized-string-${props.defaultValueLabel}-${obj[1]}`}
                label={t(translations.languages[obj[1]])}
                value={localizedValues[obj[0] as keyof LocalizedString] || ''}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleChangeLocalizedValue(obj[0] as keyof LocalizedString, event.target.value);
                }}
                onBlur={props.handleBlurLocalizedValues}
                multiline={props.multiline === true ? true : false}
                error={props.localizedValuesError ? true : false}
                required={props.localizedValueRequired === true ? true : false}
                rows={props.rows || 2}
                InputLabelProps={{
                  shrink: true,
                }}
                variant="outlined"
                color="neutral"
                type="text"
                fullWidth
              />
            );
          })}
          {props.localizedValuesError && (
            <Typography variant="caption" color="error" component="div">
              {props.localizedValuesError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLocalizedValues} color="inherit">
            {t(translations.localized_string_input.cancel_button)}
          </Button>
          <Button onClick={handleConfirmLocalizedValues} color="primary">
            {t(translations.localized_string_input.confirm_button)}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
